import { useCallback } from 'react';
import api from '../api';

const getServerMessage = (error) => {
  const data = error.response?.data;
  if (typeof data === 'string') return data.trim();
  return data?.message || data?.error || '';
};

const toUserError = (error, url = '') => {
  if (!navigator.onLine) {
    return new Error(
      'You appear to be offline. Check your internet connection and try again.'
    );
  }

  const status = error.response?.status;
  const serverMessage = getServerMessage(error);
  if (!status || error.code === 'ECONNREFUSED' || error.code === 'ETIMEDOUT') {
    return new Error(
      'The server could not be reached. Please wait a moment and try again.'
    );
  }
  if (status === 400 || status === 409 || status === 422) {
    return new Error(
      serverMessage ||
        'Some information is missing or invalid. Review the form and try again.'
    );
  }
  if (status === 401) {
    if (url === '/api/auth/login') {
      return new Error(serverMessage || 'Invalid username or password.');
    }
    return new Error('Your session has expired. Please sign in again.');
  }
  if (status === 403) {
    return new Error(
      serverMessage ||
        'You do not have permission to perform this action. Contact your administrator if access is required.'
    );
  }
  if (status === 404) {
    return new Error(
      serverMessage || 'The requested information could not be found.'
    );
  }
  if (status === 415) {
    return new Error(
      'This file or request format is not supported. Check the selected file and try again.'
    );
  }
  if (status === 429) {
    return new Error(
      'Too many requests were made in a short time. Please wait and try again.'
    );
  }
  if (status >= 500) {
    return new Error(
      serverMessage ||
        'We could not complete this action because the server encountered a problem. Please try again; if it continues, contact support.'
    );
  }
  return new Error(serverMessage || error.message || 'The request could not be completed.');
};

const logAndThrow = (method, url, error) => {
  console.error(`useApi - ${method} ${url} error:`, {
    message: error.message,
    code: error.code,
    status: error.response?.status,
    data: error.response?.data,
  });
  throw toUserError(error, url);
};

/**
 * Hook for API operations with network and server availability checks
 * @returns {Object} API methods and error handling utilities
 */
function useApi() {
  const checkNetworkConnectivity = () => {
    if (!navigator.onLine) {
      throw new Error('No network connectivity. Please check your internet connection.');
    }
  };

  const get = useCallback(async (url, config = {}) => {
    try {
      checkNetworkConnectivity();
      const response = await api.get(url, config);
      if (response.status >= 500) {
        throw new Error('Backend server is unavailable. Please try again later.');
      }
      // Normalize response for /api/communities and /api/communities/active
      if (url.includes('/api/communities')) {
        if (Array.isArray(response.data)) {
          return response.data;
        } else if (response.data?.content) {
          // Handle paginated response (Page<CommunityDTO>)
          return response.data.content;
        } else {
          console.warn(`useApi - Unexpected response format for ${url}:`, response.data);
          return [];
        }
      }
      return response.data;
    } catch (error) {
      logAndThrow('GET', url, error);
    }
  }, []);

  const post = useCallback(async (url, data = {}, config = {}) => {
    try {
      checkNetworkConnectivity();
      const response = await api.post(url, data, {
        ...config,
        headers: {
          ...config.headers,
          // Avoid setting Content-Type for FormData; let browser handle it
          ...(data instanceof FormData ? {} : { 'Content-Type': 'application/json' })
        }
      });
      if (response.status >= 500) {
        throw new Error('Backend server is unavailable. Please try again later.');
      }
      return response.data;
    } catch (error) {
      logAndThrow('POST', url, error);
    }
  }, []);

  const put = useCallback(async (url, data = {}, config = {}) => {
    try {
      checkNetworkConnectivity();
      const response = await api.put(url, data, {
        ...config,
        headers: {
          ...config.headers,
          // Avoid setting Content-Type for FormData; let browser handle it
          ...(data instanceof FormData ? {} : { 'Content-Type': 'application/json' })
        }
      });
      if (response.status >= 500) {
        throw new Error('Backend server is unavailable. Please try again later.');
      }
      return response.data;
    } catch (error) {
      logAndThrow('PUT', url, error);
    }
  }, []);

  const deleteRequest = useCallback(async (url, config = {}) => {
    try {
      checkNetworkConnectivity();
      const response = await api.delete(url, config);
      if (response.status >= 500) {
        throw new Error('Backend server is unavailable. Please try again later.');
      }
      return response.data;
    } catch (error) {
      logAndThrow('DELETE', url, error);
    }
  }, []);

  return { get, post, put, delete: deleteRequest };
}

export default useApi;
